import { DISPLAY_DATE_FORMAT } from 'constants/dates';
import { ChangeEvent, Dispatch, SetStateAction, useMemo } from 'react';
import {
  debounce,
  InputAdornment,
  Skeleton,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import { Job, JobQueryParams } from 'api/jobs/jobs.types';
import { StyledTextField } from 'components/Inputs';
import { StyledTableCell, StyledTableHeadCell } from 'components/Table';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { IoSearch } from 'react-icons/io5';
import {
  jobsHeadCells,
  jobStatusLabels,
} from '../../AddAllocationDialog.const';
import { JobsSubtableData } from '../../types';
import { ToolbarContainer } from './JobSubtable.css';

type Props = {
  data: Job[];
  count: number;
  setParams: Dispatch<SetStateAction<JobQueryParams>>;
  params: JobQueryParams;
  loading: boolean;
  selectedJob: JobsSubtableData | null;
  onSelectJob: Dispatch<SetStateAction<JobsSubtableData | null>>;
};

function createTableData(
  id: number,
  code: string,
  customer: string,
  etaEtd: number, // estimated time of arrival / estimated time of departure
  description: string,
  status: string,
): JobsSubtableData {
  return {
    id,
    code,
    customer,
    etaEtd: etaEtd ? format(etaEtd, DISPLAY_DATE_FORMAT) : '',
    description,
    status,
  };
}

export const JobsSubtable = ({
  data,
  count,
  setParams,
  params,
  loading,
  selectedJob,
  onSelectJob,
}: Props) => {
  const { t } = useTranslation(['misc', 'invoice', 'currency', 'datagrid']);

  const rows = useMemo(() => data?.map((j) =>
      createTableData(
        j.id,
        j.code,
        j.customer.counterpartyShortName,
        j.tradeType === 'IMPORT' ? j.eta : j.etd,
        j.description,
        jobStatusLabels[j.status],
      ),
    ), [data]);

  const debouncedSearch = useMemo(
    () =>
      debounce(
        (e: ChangeEvent<HTMLInputElement>) =>
          setParams((prev) => ({ ...prev, filterText: e.target.value })),
        500,
      ),
    [],
  );

  const handleChangePage = (_: unknown, newPage: number) =>
    setParams((prev) => ({ ...prev, pageNumber: newPage + 1 }));

  return (
    <TableContainer>
      <ToolbarContainer>
        <StyledTextField
          placeholder={t('placeholder', { ns: 'datagrid' })}
          type='text'
          size='small'
          padding='dense'
          margin='dense'
          width={250}
          onChange={debouncedSearch}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IoSearch />
              </InputAdornment>
            ),
          }}
        />
      </ToolbarContainer>
      {rows && (
        <>
          <Table aria-labelledby='jobs' size='medium'>
            <TableHead>
              <TableRow>
                {jobsHeadCells.map((cell) => (
                  <StyledTableHeadCell
                    key={cell.id}
                    align='left'
                    height='condensed'
                  >
                    {cell.label}
                  </StyledTableHeadCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows?.map((row) => (
                  <TableRow
                    hover
                    key={row.id}
                    onClick={() => onSelectJob(row)}
                    selected={selectedJob?.id === row?.id}
                  >
                    {jobsHeadCells.map((cell) => (
                      <StyledTableCell
                        key={cell.id}
                        align={cell.align || 'left'}
                        height='condensed'
                      >
                        {!loading ? row[cell.id] : <Skeleton />}
                      </StyledTableCell>
                    ))}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            component='div'
            count={count}
            rowsPerPage={10}
            rowsPerPageOptions={[10]}
            page={params.pageNumber - 1}
            onPageChange={handleChangePage}
            labelDisplayedRows={({ from, to, count }) => `${from} - ${to} ${t('of', { ns: 'datagrid' })} ${count}`}
          />
        </>
      )}
    </TableContainer>
  );
};
